import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Row,
  Col,
  Typography,
  Button,
  notification,
  Layout,
  Form,
  Input,
} from 'antd';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import WhiteBlueLogo from '@/assets/images/LogoOficial.png';
import { authOperations } from '@/state/ducks/auth';
import './style.less';
import { useDisclosure } from '@/hooks';
import ForgotPasswordModal from '@/components/Login/ForgotPasswordModal';
import { getUserFromLocalStorage } from '@/utils/user';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import { store } from '@/Root';
import { AxiosError } from 'axios';
import { useMainContext } from '@/contexts/MainContextProvider';
import { convertParams } from '@/utils/queryParams';
import { isAppPortocar } from '@/index';
import UserGuiding from '@/core/UserGuinding';
import { UG_HASH } from '@/utils/user/constants';

const LoginPage = () => {
  const history = useHistory();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [forgotPasswordModalVisible, forgotPasswordModalVisibleAction] =
    useDisclosure();

  const {
    modalWithoutMoviments,
    openMarketingPublication,
    popoverNotification,
  } = useMainContext();

  const search = convertParams<{ redirect: string }>({ redirect: 'string' });

  const userData = getUserFromLocalStorage();

  useEffect(() => {
    if (userData.accessToken && !loading) {
      const tokenData = jwtDecode<{ exp?: number }>(userData.accessToken);

      if (tokenData && tokenData.exp) {
        const timestamp = tokenData.exp * 1000;

        if (
          !moment(timestamp).isValid() ||
          moment(moment(timestamp).subtract(30, 'minutes')).isSameOrBefore(
            moment(),
          )
        ) {
          refreshToken();
        } else if (
          moment(timestamp).isValid() &&
          moment(moment(timestamp).subtract(30, 'minutes')).isAfter(moment())
        ) {
          history.replace('/app/dashboard');
        }
      }
    }
  }, [userData, loading]);

  async function refreshToken() {
    try {
      await store.dispatch(authOperations.handleRefreshToken());
      history.replace('/app/dashboard');
    } catch {}
  }

  const handleLoginSubmit = async () => {
    setLoading(true);

    try {
      const values = await form.validateFields();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await dispatch(async (ctx: any) => {
        await authOperations.handleLogin({
          username: values.username,
          password: values.password,
        })(ctx);

        UserGuiding.identifyUser(localStorage.getItem(UG_HASH) || undefined);

        if (modalWithoutMoviments.show) {
          modalWithoutMoviments.toggle();
        }

        openMarketingPublication();
        popoverNotification.action.open();

        if (!!search?.redirect) {
          return history.push(search.redirect);
        } else {
          history.push('/app/dashboard');
        }
      });
    } catch (err) {
      const error = err as AxiosError;

      const description: string | undefined =
        error.response?.data.error_description;

      if (!!description) {
        notification.error({
          message: 'Falha ao realizar o login',
          description: description,
        });
      } else {
        notification.error({
          message: 'Falha ao realizar o login',
          description: 'Não foi possível autenticar o usuário',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const variants = {
    initial: { opacity: 0 },
    in: { opacity: 1 },
    out: { opacity: 0 },
  };

  return (
    <>
      <Helmet title="Login" />

      <motion.div initial="initial" animate="in" exit="out" variants={variants}>
        <Row className="login-page">
          <Col md={24} lg={12} className="banner-wrapper">
            <Link to="/">
              <img
                src={
                  isAppPortocar
                    ? '/images/Logo_PortoCar_branca.svg'
                    : WhiteBlueLogo
                }
                alt="logo"
                style={{ width: '555px' }}
              />
            </Link>
          </Col>

          <Col md={24} lg={12} className="form-wrapper">
            <Layout>
              <Layout.Content>
                <div />

                <div className="form">
                  <Typography.Title className="app-title">
                    Login
                  </Typography.Title>

                  <Typography.Title level={3} className="welcome-text">
                    Gerencie sua oficina de forma fácil e aumente sua
                    lucratividade.
                  </Typography.Title>

                  <Form layout="vertical" form={form}>
                    <FloatingFormItem
                      label="Usuário"
                      name="username"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </FloatingFormItem>

                    <FloatingFormItem
                      label="Senha"
                      name="password"
                      rules={[{ required: true }]}
                    >
                      <Input.Password />
                    </FloatingFormItem>

                    <Form.Item style={{ marginTop: '36px' }}>
                      <Button
                        className="submit-button"
                        type="primary"
                        onClick={handleLoginSubmit}
                        loading={loading}
                        htmlType="submit"
                      >
                        Entrar
                      </Button>
                    </Form.Item>

                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        type="link"
                        size="large"
                        onClick={forgotPasswordModalVisibleAction.open}
                        style={{ padding: 0, margin: 0 }}
                      >
                        Esqueci minha senha
                      </Button>
                    </div>
                  </Form>
                </div>

                <Typography>
                  {'Ao usar esta aplicação, você está concordando com nossa '}
                  <span>
                    <Link to="/privacyPolicy">política de privacidade</Link>
                  </span>
                </Typography>
              </Layout.Content>
            </Layout>
          </Col>
        </Row>
      </motion.div>

      <ForgotPasswordModal
        open={forgotPasswordModalVisible}
        onCancel={forgotPasswordModalVisibleAction.close}
      />
    </>
  );
};

export default LoginPage;
